import { Pin, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useIsMounted } from '@avanta/shared/hooks/useIsMounted';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { scrollToElementById } from '@avanta/shared/utils/common';
import { isHtmlMessage } from '@avanta/shared/utils/text';
import { RichMessageText } from '../Message/components/Content/RichMessageText';

export const PinnedMessage = (): React.ReactElement | null => {
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const pinnedMessage = useMessagesStore(state =>
    activeChannelId ? (state.pinnedMessages.get(activeChannelId) ?? null) : null
  );
  const { unpinMessage } = useMessagesStore();
  const isMounted = useIsMounted();
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  if (!pinnedMessage) return null;

  const handleOnUnpin = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.stopPropagation();
    if (!activeChannelId || !isMounted()) return;
    try {
      await unpinMessage(activeChannelId);
    } catch {
      toast.error(t('Chat.Messages.unpin.error'));
    }
  };

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    scrollToElementById(pinnedMessage.timetoken);
  };

  return (
    <div className="p-4 border-b border-plum-200 bg-white relative flex justify-between gap-2 items-center w-full">
      <button
        type="button"
        onClick={handleOnClick}
        className="flex items-center gap-2 flex-1 text-left"
      >
        <span className="size-4">
          <Pin className="size-4 text-denim-600" />
        </span>
        <span className="text-sm text-denim-600 line-clamp-1">
          {isHtmlMessage(pinnedMessage.text) ? (
            <RichMessageText html={pinnedMessage.text} />
          ) : (
            pinnedMessage.text
          )}
        </span>
      </button>
      <Button
        variant="ghost"
        icon="only"
        onClick={handleOnUnpin}
        className="size-4.5 hover:bg-transparent hover:[&>svg]:stroke-[2.5px]"
      >
        <X />
      </Button>
    </div>
  );
};
